import { Form, Formik, FormikConfig } from 'formik';

import { Button, Container, Input } from 'components/atoms';
import { DescriptiveInputWrapper, FormInput } from 'components/molecules';
import Popup from 'components/molecules/popup';
import { format } from 'date-fns';
import { useHasPermission } from 'hooks';
import { useStreamFileDownload } from 'hooks/useStreamFileDownload';
import i18next from 'i18next';
import _ from 'lodash';
import { useStoreState } from 'providers/StoreStateContext';
import { useTranslation } from 'react-i18next';
import {
  PiFileArrowDown,
  PiFloppyDisk,
  PiPlus,
  PiProhibit,
} from 'react-icons/pi';
import {
  useAddEans,
  useGetEanCount,
  useGetEanReport,
} from 'services/codesDistributor/ean';
import { useGetEmails } from 'services/email/email';
import { useGetLicenceConfiguration } from 'services/settings/individual';
import { ISettingsForm } from 'services/settings/application/types';
import { SETTLEMENT_PERIOD_TYPE_ENUM } from 'services/settlementPeriods/types';
import { useGetThemes } from 'services/theme';
import { DOCUMENT_NUMERATION_TYPE, MONTH } from 'services/types';
import { THEME_TYPE } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { II18nextPrefix } from 'types/components/components.interface';
import { useEnumToSelectOptions } from 'utils/components/inputs/select';
import { getCountriesPhoneNumberPrefixOptionValues } from 'utils/global/countries.utils';
import { formatDateToInput } from 'utils/global/date.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { boolean, number, object, string } from 'yup';
import { IFormProps } from '../types';

export const settingsInitialValues: ISettingsForm = {
  isrcPrefix: '',
  phoneNumberPrefix: '',
  defaultApplicationLanguage: '',
  showOnlyVerifiedArtists: false,
  showOnlyVerifiedTracks: false,
  showOnlyVerifiedMerch: false,
  showNotActiveSalesChannels: false,
  allowContractsWithSameLabel: false,
  extensionContract: false,
  allowDetailedReport: false,
  showOnlySettlementPeriodsWithReports: false,
  showOnlyVerifiedMpd: false,
  showOnlyVerifiedMpp: false,
  enableArtistFullName: false,
  residenceCertificateExpirationRangeInMonths: 1,
  showOnlyVerifiedVideos: false,
  autoRenewLabelReports: false,
  enableSettlementDeduction: false,
  settlementDeductionName: null,
  settlementDeductionPercentage: null,
  periodType: -1,
  settlementStartMonth: -1,
  artistDescriptionMaxLength: 0,
  expirationRangeInMonths: 1,
  isrcStartNumber: null,
  isrcGenerationStartDate: '',
  remainingEansNotificationThreshold: 0,
  currentTheme: null,
  defaultThemeMode: THEME_TYPE.light,
  canChangeThemeMode: true,
  documentsConfiguration: {
    settlementShortcut: '',
    settlementNumerationType: DOCUMENT_NUMERATION_TYPE.unknown,
    labelReportShortcut: '',
    labelReportNumerationType: DOCUMENT_NUMERATION_TYPE.unknown,
    advanceRepaymentShortcut: '',
    advanceRepaymentNumerationType: DOCUMENT_NUMERATION_TYPE.unknown,
    advanceFinalShortcut: '',
    advanceFinalNumerationType: DOCUMENT_NUMERATION_TYPE.unknown,
    advanceProposalShortcut: '',
    advanceProposalNumerationType: DOCUMENT_NUMERATION_TYPE.unknown,
    costRepaymentShortcut: '',
    costRepaymentNumerationType: DOCUMENT_NUMERATION_TYPE.unknown,
    ownerReportShortcut: '',
    ownerReportNumerationType: DOCUMENT_NUMERATION_TYPE.unknown,
    trackReportShortcut: '',
    trackReportNumerationType: DOCUMENT_NUMERATION_TYPE.unknown,
  },
  contractEmailConfiguration: null,
  allowSendContracts: false,
  sendContractsAutomatically: false,
  labelReportEmailConfiguration: null,
  allowSendLabelReports: false,
  sendLabelReportsAutomatically: false,
  settlementEmailConfiguration: null,
  allowSendSettlements: false,
  sendSettlementsAutomatically: false,
};

const artistValidator = object().shape({
  phoneNumberPrefix: string().required(
    i18next.t('formik-error.phone-number-prefix-is-required')
  ),

  defaultApplicationLanguage: string().required(
    i18next.t('formik-error.default-application-language-is-required')
  ),
  currentTheme: string().required(i18next.t('formik-error.required')),
  defaultThemeMode: string().required(i18next.t('formik-error.required')),
  showOnlyVerifiedArtists: boolean(),
  showOnlyVerifiedTracks: boolean(),
  settlementDeductionName: string().when('enableSettlementDeduction', {
    is: true,
    then: schema => schema.required(i18next.t('formik-error.required')),
    otherwise: schema => schema.nullable().notRequired(),
  }),
  settlementDeductionPercentage: number().when('enableSettlementDeduction', {
    is: true,
    then: schema =>
      schema
        .typeError(i18next.t('formik-error.required'))
        .required(i18next.t('formik-error.required'))
        .min(0, i18next.t('formik-error.min-percent-value', { value: 0 }))
        .max(100, i18next.t('formik-error.max-percent-value', { value: 100 })),
    otherwise: schema => schema.nullable().notRequired(),
  }),
  allowDetailedReport: boolean(),
  remainingEansNotificationThreshold: number()
    .typeError(i18next.t('formik-error.must-be-number'))
    .required(i18next.t('formik-error.required'))
    .positive(i18next.t('formik-error.amount-must-be-positive')),
});

export interface ISettingsFormProps
  extends FormikConfig<ISettingsForm>,
    IFormProps,
    II18nextPrefix {}

const ApplicationSettingsForm = ({
  i18nextPrefix,

  topbarContent,
  tittle,
  status,
  initialValues = settingsInitialValues,
  disableTabs,
  backPath,
  customBackItem,
  ...props
}: ISettingsFormProps) => {
  const { t } = useTranslation();

  const { getLanguagesOptions } = useStoreState();

  const eanCount = useGetEanCount();

  const addEans = useAddEans();

  const { setFile } = useStreamFileDownload(useGetEanReport);

  const themes = useGetThemes();

  const { getSelectOptions } = useEnumToSelectOptions();

  const emailConfigurations = useGetEmails();
  const licenceConfiguration = useGetLicenceConfiguration();

  const hasPermission = useHasPermission();

  const emailSelectOptions =
    emailConfigurations.data?.map(e => ({ value: e.id, label: e.name })) ?? [];
  const cancleDate = (setValues: any) => {
    setValues(initialValues);
  };

  const isInitialValueEqualsForm = (form: ISettingsForm) =>
    _.isEqual(initialValues, form);

  return (
    <Formik
      {...props}
      validateOnChange
      validateOnBlur
      validateOnMount
      validationSchema={artistValidator}
      initialStatus={status?.displayMode}
      enableReinitialize
      initialValues={initialValues}>
      {({ setValues, values, status, errors, setFieldValue }) => (
        <>
          <Form className='flex w-full h-full gap-[30px] flex-wrap'>
            <div className='flex flex-col gap-[30px] w-[370px]'>
              <DescriptiveInputWrapper
                header={t('setting.application.applicationlanguage')}
                description={t(
                  'setting.application.applicationlanguageDescription'
                )}>
                <FormInput.Select
                  name='defaultApplicationLanguage'
                  label={t('input.label.language')}
                  options={getLanguagesOptions()}
                />
              </DescriptiveInputWrapper>
              <DescriptiveInputWrapper
                header={t('setting.application.theme')}
                description={t('setting.application.themeDescription')}>
                <FormInput.Select
                  name='currentTheme'
                  label={t('input.label.currentTheme')}
                  options={themes.data?.map(theme => ({
                    value: theme.id,
                    label:
                      theme.name === 'Default'
                        ? t('setting.application.default')
                        : theme.name,
                  }))}
                  isClearable={false}
                />
                <FormInput.Select
                  name='defaultThemeMode'
                  label={t('input.label.defaultThemeMode')}
                  options={getSelectOptions(THEME_TYPE, 'enum.THEME_TYPE')}
                  isClearable={false}
                />
                <FormInput.Checkbox
                  name='canChangeThemeMode'
                  label={t('input.label.canChangeThemeMode')}
                />
              </DescriptiveInputWrapper>
              <DescriptiveInputWrapper
                header={`${t('setting.application.eans')}: ${
                  eanCount.data?.toLocaleString().replaceAll(',', ' ') ?? '-'
                }`}
                headerContent={
                  hasPermission('CodesDistributorPermissionsLIST**', 1) && (
                    <div className='flex'>
                      <Popup.CodesParser
                        header={t('setting.application.eans-header')}
                        onSubmit={async (values, helpers, actions) =>
                          await addEans.mutateAsync(values.codes, {
                            onSuccess: () => {
                              eanCount.refetch();
                              actions?.close();
                            },
                            onError: error => onErrorShowAllErrorsToast(error),
                          })
                        }
                        trigger={
                          <Button
                            onlyIcon
                            size='compact'
                            style={{ marginRight: 4 }}>
                                                        <Button.Description description={t('button.add')} />

                                                        <Button.Icon icon={<PiPlus />} />
                          </Button>
                        }
                      />
                    </div>
                  )
                }
                description={t('setting.application.eansDescription')}>
                <FormInput.Number
                  name='remainingEansNotificationThreshold'
                  label={t('input.label.remainingEansNotificationThreshold')}
                />
                <Button
                  onlyIcon
                  isClear
                  fullWidth
                  onClick={() =>
                    setFile({
                      id: formatDateToInput(new Date(), true),
                      name: `${t('button.ean-raport').replaceAll(
                        ' ',
                        '_'
                      )}_${format(new Date(), 'dd_MM_yyyy_hh:mm')}`,
                      extension: 'xlsx',
                    })
                  }>
                  <Button.Text text={t('button.download-report')} />
                                    <Button.Description description={t('button.addFile')} />

                                    <Button.Icon icon={<PiFileArrowDown />} />
                </Button>
              </DescriptiveInputWrapper>
              <DescriptiveInputWrapper
                header={t('setting.application.prefixes')}
                description={t('setting.application.prefixesDescription')}>
                <Input.ComplexWrapper sizes={[50, 50]}>
                  <FormInput.Text
                    name='isrcPrefix'
                    label={t('input.label.isrc')}
                  />
                  <FormInput.Text
                    type='number'
                    name='isrcStartNumber'
                    label={t('input.label.start-number')}
                    displayMode={initialValues.isrcStartNumber !== null}
                  />
                </Input.ComplexWrapper>
                <FormInput.Text
                  type='datetime-local'
                  name='isrcGenerationStartDate'
                  label={t('input.label.isrcGenerationStartDate')}
                />
                <FormInput.Select
                  name='phoneNumberPrefix'
                  label={t('input.label.phoneNumber')}
                  isCreatable
                  options={getCountriesPhoneNumberPrefixOptionValues()}
                />
              </DescriptiveInputWrapper>
              <DescriptiveInputWrapper
                header={t('setting.application.selectionLists')}
                description={t(
                  'setting.application.selectionListsDescription'
                )}>
                <FormInput.Checkbox
                  name='showOnlyVerifiedArtists'
                  label={t('input.label.showOnlyVerifiedArtists')}
                />
                <FormInput.Checkbox
                  name='showOnlyVerifiedTracks'
                  label={t('input.label.showOnlyVerifiedTracks')}
                />
                <FormInput.Checkbox
                  name='showOnlyVerifiedMerch'
                  label={t('input.label.showOnlyVerifiedMerch')}
                />
                <FormInput.Checkbox
                  name='showNotActiveSalesChannels'
                  label={t('input.label.showNotActiveSalesChannels')}
                />
                <FormInput.Checkbox
                  name='showOnlySettlementPeriodsWithReports'
                  label={t('input.label.showOnlySettlementPeriodsWithReports')}
                />
                <FormInput.Checkbox
                  name='showOnlyVerifiedMpd'
                  label={t('input.label.showOnlyVerifiedMpd')}
                />
                <FormInput.Checkbox
                  name='showOnlyVerifiedMpp'
                  label={t('input.label.showOnlyVerifiedMpp')}
                />
                <FormInput.Checkbox
                  name='showOnlyVerifiedVideos'
                  label={t('input.label.showOnlyVerifiedVideos')}
                />
              </DescriptiveInputWrapper>
            </div>
            <div className='flex flex-col gap-[30px] w-[400px]'>
              <DescriptiveInputWrapper header={t('setting.application.artist')}>
                <FormInput.Text
                  type='number'
                  name='artistDescriptionMaxLength'
                  label={t('input.label.description-max-length')}
                />
                <FormInput.Checkbox
                  name='enableArtistFullName'
                  label={t('input.label.enableArtistFullName')}
                />
              </DescriptiveInputWrapper>
              <DescriptiveInputWrapper
                header={t('setting.application.contract')}>
                <FormInput.Select
                  name='expirationRangeInMonths'
                  label={t('setting.application.expirationRangeInMonths')}
                  helperText={t(
                    'setting.application.expirationRangeInMonths-helper'
                  )}
                  isClearable={false}
                  isCreatable={false}
                  options={new Array(12)
                    .fill('')
                    .map((_, i) => ({ value: i + 1, label: i + 1 }))}
                />
                <FormInput.Checkbox
                  name='allowContractsWithSameLabel'
                  label={t('setting.application.allowContractsWithSameLabel')}
                />
                <FormInput.Checkbox
                  name='extensionContract'
                  label={t('setting.application.extensionContract')}
                />
                {licenceConfiguration.data?.isDetailedReportingEnabled && (
                  <FormInput.Checkbox
                    name='allowDetailedReport'
                    label={t('setting.application.allowDetailedReport')}
                  />
                )}
                <FormInput.Select
                  name='residenceCertificateExpirationRangeInMonths'
                  label={t('setting.application.residenceCertificateExpirationRangeInMonths')}
                  helperText={t(
                    'setting.application.residenceCertificateExpirationRangeInMonths-helper'
                  )}
                  isClearable={false}
                  isCreatable={false}
                  options={new Array(3)
                    .fill('')
                    .map((_, i) => ({ value: i + 1, label: i + 1 }))}
                />
              </DescriptiveInputWrapper>
              <DescriptiveInputWrapper
                header={t('setting.application.settlement')}
                description={t('setting.application.settlementDescription')}>
                <Input.ComplexWrapper sizes={[50, 50]}>
                  <FormInput.Select
                    name='periodType'
                    label={t('input.label.period')}
                    options={getSelectOptions(
                      SETTLEMENT_PERIOD_TYPE_ENUM,
                      'enum.SETTLEMENT_PERIOD_TYPE'
                    )}
                  />
                  <FormInput.Select
                    name='settlementStartMonth'
                    label={t('input.label.startMonth')}
                    options={getSelectOptions(MONTH, 'enum.MONTH')}
                    displayMode={initialValues.settlementStartMonth !== null}
                  />
                </Input.ComplexWrapper>
              </DescriptiveInputWrapper>
              <DescriptiveInputWrapper
                header={t('setting.application.settlementDeduction')}
                description={t(
                  'setting.application.settlementDeductionDescription'
                )}>
                <FormInput.Checkbox
                  name='enableSettlementDeduction'
                  label={t('input.label.enableSettlementDeduction')}
                />
                <FormInput.Text
                  name='settlementDeductionName'
                  label={t('input.label.name')}
                  disabled={!values.enableSettlementDeduction}
                />
                <FormInput.Number
                  name='settlementDeductionPercentage'
                  label={t('input.label.settlementDeductionPercentage')}
                  placeholder='0 %'
                  suffix=' %'
                  disabled={!values.enableSettlementDeduction}
                />
              </DescriptiveInputWrapper>
              {/* <DescriptiveInputWrapper
                header={t('setting.application.currency')}
                description={t('setting.application.currencyDescription')}>
                <FormInput.Select
                  name='defaultCurrencyId'
                  label={t('input.label.currency')}
                  options={getCurrenciesOptions()}
                />
              </DescriptiveInputWrapper> */}
              <DescriptiveInputWrapper
                header={t('setting.application.documents')}
                description={t('setting.application.documentsDescription')}>
                <Input.ComplexWrapper sizes={[50, 50]}>
                  <FormInput.Text
                    name='documentsConfiguration.settlementShortcut'
                    label={t('input.label.settlementShortcut')}
                  />
                  <FormInput.Select
                    name='documentsConfiguration.settlementNumerationType'
                    label={t('input.label.numeration')}
                    options={getSelectOptions(
                      DOCUMENT_NUMERATION_TYPE,
                      'enum.DOCUMENT_NUMERATION_TYPE'
                    )}
                  />
                </Input.ComplexWrapper>
                <div />
                <Input.ComplexWrapper sizes={[50, 50]}>
                  <FormInput.Text
                    name='documentsConfiguration.labelReportShortcut'
                    label={t('input.label.labelReportShortcut')}
                  />
                  <FormInput.Select
                    name='documentsConfiguration.labelReportNumerationType'
                    label={t('input.label.numeration')}
                    options={getSelectOptions(
                      DOCUMENT_NUMERATION_TYPE,
                      'enum.DOCUMENT_NUMERATION_TYPE'
                    )}
                  />
                </Input.ComplexWrapper>
                <div />
                <Input.ComplexWrapper sizes={[50, 50]}>
                  <FormInput.Text
                    name='documentsConfiguration.costRepaymentShortcut'
                    label={t('input.label.costRepaymentShortcut')}
                  />
                  <FormInput.Select
                    name='documentsConfiguration.costRepaymentNumerationType'
                    label={t('input.label.numeration')}
                    options={getSelectOptions(
                      DOCUMENT_NUMERATION_TYPE,
                      'enum.DOCUMENT_NUMERATION_TYPE'
                    )}
                  />
                </Input.ComplexWrapper>
                <div />
                <Input.ComplexWrapper sizes={[50, 50]}>
                  <FormInput.Text
                    name='documentsConfiguration.advanceRepaymentShortcut'
                    label={t('input.label.advanceRepaymentShortcut')}
                  />
                  <FormInput.Select
                    name='documentsConfiguration.advanceRepaymentNumerationType'
                    label={t('input.label.numeration')}
                    options={getSelectOptions(
                      DOCUMENT_NUMERATION_TYPE,
                      'enum.DOCUMENT_NUMERATION_TYPE'
                    )}
                  />
                </Input.ComplexWrapper>
                <div />
                <Input.ComplexWrapper sizes={[50, 50]}>
                  <FormInput.Text
                    name='documentsConfiguration.advanceFinalShortcut'
                    label={t('input.label.advanceFinalShortcut')}
                  />
                  <FormInput.Select
                    name='documentsConfiguration.advanceFinalNumerationType'
                    label={t('input.label.numeration')}
                    options={getSelectOptions(
                      DOCUMENT_NUMERATION_TYPE,
                      'enum.DOCUMENT_NUMERATION_TYPE'
                    )}
                  />
                </Input.ComplexWrapper>
                <div />
                <Input.ComplexWrapper sizes={[50, 50]}>
                  <FormInput.Text
                    name='documentsConfiguration.advanceProposalShortcut'
                    label={t('input.label.advanceProposalShortcut')}
                  />
                  <FormInput.Select
                    name='documentsConfiguration.advanceProposalNumerationType'
                    label={t('input.label.numeration')}
                    options={getSelectOptions(
                      DOCUMENT_NUMERATION_TYPE,
                      'enum.DOCUMENT_NUMERATION_TYPE'
                    )}
                  />
                </Input.ComplexWrapper>
                <div />
                <Input.ComplexWrapper sizes={[50, 50]}>
                  <FormInput.Text
                    name='documentsConfiguration.ownerReportShortcut'
                    label={t('input.label.ownerReportShortcut')}
                  />
                  <FormInput.Select
                    name='documentsConfiguration.ownerReportNumerationType'
                    label={t('input.label.numeration')}
                    options={getSelectOptions(
                      DOCUMENT_NUMERATION_TYPE,
                      'enum.DOCUMENT_NUMERATION_TYPE'
                    )}
                  />
                </Input.ComplexWrapper>
                <div />
                <Input.ComplexWrapper sizes={[50, 50]}>
                  <FormInput.Text
                    name='documentsConfiguration.trackReportShortcut'
                    label={t('input.label.trackReportShortcut')}
                  />
                  <FormInput.Select
                    name='documentsConfiguration.trackReportNumerationType'
                    label={t('input.label.numeration')}
                    options={getSelectOptions(
                      DOCUMENT_NUMERATION_TYPE,
                      'enum.DOCUMENT_NUMERATION_TYPE'
                    )}
                  />
                </Input.ComplexWrapper>
              </DescriptiveInputWrapper>
            </div>
            <div className='flex flex-col gap-[30px] w-[350px]'>
              <DescriptiveInputWrapper
                header={t('setting.application.emailConfiguration')}
                description={t(
                  'setting.application.emailConfigurationDescription'
                )}
                omitContainerContent>
                <Container.Content>
                  <FormInput.Select
                    name='contractEmailConfiguration'
                    label={t('input.label.contract')}
                    options={emailSelectOptions}
                  />
                  <FormInput.Checkbox
                    name='allowSendContracts'
                    label={t('input.label.allowSend')}
                  />
                  <FormInput.Checkbox
                    name='sendContractsAutomatically'
                    label={t('input.label.sendAutomaticly')}
                  />
                </Container.Content>
                <Container.Content>
                  <FormInput.Select
                    name='labelReportEmailConfiguration'
                    label={t('input.label.labelReportShortcut')}
                    options={emailSelectOptions}
                  />
                  <FormInput.Checkbox
                    name='allowSendLabelReports'
                    label={t('input.label.allowSend')}
                  />
                  <FormInput.Checkbox
                    name='sendLabelReportsAutomatically'
                    label={t('input.label.sendAutomaticly')}
                  />
                  <FormInput.Checkbox
                    name='autoRenewLabelReports'
                    label={t('input.label.autoRenewLabelReports')}
                  />
                </Container.Content>
                <Container.Content>
                  <FormInput.Select
                    name='settlementEmailConfiguration'
                    label={t('input.label.settlementShortcut')}
                    options={emailSelectOptions}
                  />
                  <FormInput.Checkbox
                    name='allowSendSettlements'
                    label={t('input.label.allowSend')}
                  />
                  <FormInput.Checkbox
                    name='sendSettlementsAutomatically'
                    label={t('input.label.sendAutomaticly')}
                  />
                </Container.Content>
              </DescriptiveInputWrapper>
            </div>
            {!isInitialValueEqualsForm(values) && (
              <div className='buttons'>
                <Button
                  variant={BUTTON_VARIANT.warning}
                  onClick={() => cancleDate(setValues)}>
                  <Button.Icon icon={<PiProhibit transform='scale(-1,1)' />} />
                  <Button.Text text={t(`enum.TEMPLATE_TYPE_SETTINGS.cancle`)} />
                </Button>
                <Button type='submit' variant={BUTTON_VARIANT.submit}>
                  <Button.Icon
                    icon={<PiFloppyDisk transform='scale(-1,1)' />}
                  />
                  <Button.Text text={t(`enum.TEMPLATE_TYPE_SETTINGS.submit`)} />
                </Button>
              </div>
            )}
          </Form>
        </>
      )}
    </Formik>
  );
};

export default ApplicationSettingsForm;
